import React, { type ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';

import { cn } from '../lib/utils';

export interface InteractiveCardCollectionItem {
  id: string;
  href?: string;
  title: string;
  titleContent?: ReactNode;
  /** Full governed card content for complex linked cards. */
  content?: ReactNode;
  description?: string;
  descriptionContent?: ReactNode;
  body?: ReactNode;
  eyebrow?: string;
  leading?: ReactNode;
  metadata?: ReactNode;
  trailing?: ReactNode;
  ariaLabel?: string;
}

export interface InteractiveCardCollectionProps {
  items: readonly InteractiveCardCollectionItem[];
  ariaLabel: string;
  emptyState?: ReactNode;
  columns?: 1 | 2 | 3;
  className?: string;
  itemClassName?: string;
  experienceEvidenceRevision?: string;
}

const columnClasses = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 md:grid-cols-2',
  3: 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3',
} as const;

/**
 * Canonical linked-card collection for small navigable result sets.
 *
 * Use DataGrid when comparison, sorting, filtering or row density matters.
 * This component owns semantic list/link structure, focus treatment and the
 * complete-card hit target without introducing nested interactive controls.
 */
export function InteractiveCardCollection({
  items,
  ariaLabel,
  emptyState = null,
  columns = 2,
  className,
  itemClassName,
  experienceEvidenceRevision,
}: InteractiveCardCollectionProps) {
  if (items.length === 0) return <>{emptyState}</>;

  return (
    <section aria-label={ariaLabel} className={className} data-interactive-card-collection>
      <ul className={cn('grid gap-3', columnClasses[columns])} role="list">
        {items.map((item) => (
          <li
            key={item.id}
            className="min-w-0"
            data-experience-entity-id={experienceEvidenceRevision ? item.id : undefined}
            data-experience-evidence-revision={experienceEvidenceRevision}
          >
            {item.href ? (
              <a
                href={item.href}
                aria-label={item.ariaLabel}
                className={cn(
                  'group flex h-full min-w-0 items-start gap-3 rounded-xl border border-border bg-surface-base p-4 shadow-sm transition-colors hover:border-statura-green-accessible hover:bg-status-success/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible focus-visible:ring-offset-2',
                  itemClassName,
                )}
                data-interactive-card-link
              >
                <InteractiveCardContent item={item} />
              </a>
            ) : (
              <div
                className={cn(
                  'flex h-full min-w-0 items-start gap-3 rounded-xl border border-border bg-surface-base p-4 shadow-sm',
                  itemClassName,
                )}
                data-interactive-card-static
              >
                <InteractiveCardContent item={item} linked={false} />
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function InteractiveCardContent({
  item,
  linked = true,
}: {
  item: InteractiveCardCollectionItem;
  linked?: boolean;
}) {
  if (item.content) return <>{item.content}</>;
  return (
    <>
      {item.leading ? <span className="shrink-0">{item.leading}</span> : null}
      <div className="min-w-0 flex-1">
        {item.eyebrow ? (
          <span className="block text-xs font-semibold uppercase tracking-wide text-text-muted">
            {item.eyebrow}
          </span>
        ) : null}
        <span className="block break-words font-heading text-card-title text-text-heading group-hover:text-statura-green-accessible">
          {item.titleContent ?? item.title}
        </span>
        {item.descriptionContent || item.description ? (
          <span className="mt-1 block break-words text-sm leading-relaxed text-text-body">
            {item.descriptionContent ?? item.description}
          </span>
        ) : null}
        {item.body ? <div className="mt-4">{item.body}</div> : null}
        {item.metadata ? (
          <span className="mt-3 block text-xs text-text-muted">{item.metadata}</span>
        ) : null}
      </div>
      {item.trailing ??
        (linked ? (
          <ArrowRight
            className="mt-1 h-4 w-4 shrink-0 text-text-muted group-hover:text-statura-green-accessible"
            aria-hidden="true"
          />
        ) : null)}
    </>
  );
}
